import { getBenefitDelays } from '@ben/columbia/api';
import { parseDateValue } from '@ben/common/date';
import Checkbox from '@ben/orion/checkbox';
import DateInput from '@ben/orion/date-input';
import type { ISelectOption } from '@ben/orion/deprecated-select';
import Input from '@ben/orion/input';
import Select from '@ben/orion/select/proxy';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type {
  IModularBenefitDetail,
  IModularBenefitMessages,
} from '@ben/shared/types/columbia';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { memo, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';

const POLICY_LENGTH_MONTHS_MIN = 1;
const POLICY_LENGTH_MONTHS_MAX = 36;
const POLICY_LENGTH_MONTHS_HINT =
  'Only set for renewing policy-style benefits (e.g. life insurance). When set, the renewal date is calculated from start date + policy length.';

const DELAY_REFERENCE_MODE_HINT =
  'Which timestamp the activation delay is measured from. Defaults to the opt-in date. Approval date re-anchors the same configured delay to when the enrolment is approved, so the resulting start date is payroll-period dependent and is not necessarily in the approval month. Fixed-date overrides still take precedence, and the start date can never move earlier than under the opt-in date.';

const delayReferenceModeOptions: ISelectOption[] = [
  { key: 'OPT_IN', label: 'Opt-in date (default)' },
  { key: 'APPROVAL', label: 'Approval date' },
];

interface EnrolmentSettingsProps {
  warnings: IModularBenefitMessages;
  successes: IModularBenefitMessages;
}

const EnrolmentSettings = ({ warnings, successes }: EnrolmentSettingsProps) => {
  const { data: benefitDelays } = useQuery(
    ['benefit-delays'],
    getBenefitDelays
  );

  const benefitDelayOptions: ISelectOption[] = useMemo(
    () =>
      benefitDelays?.map((delay) => ({
        key: delay.id.toString(),
        label: delay.name,
      })) || [],
    [benefitDelays]
  );

  const { control } = useFormContext<IModularBenefitDetail>();
  const [isActive, startDate, policyLengthMonths] = useWatch({
    control,
    name: ['active', 'startDate', 'policyLengthMonths'],
    exact: true,
  });

  const isPolicyLengthSet = Boolean(policyLengthMonths);

  return (
    <Container>
      <HeaderContainer>
        <Typography variant={TypographyVariant.h2}>
          Enrolment Settings
        </Typography>
        <Typography variant={TypographyVariant.bodyMd1}>
          All fields below are required, unless specified optional.
        </Typography>
      </HeaderContainer>
      <ContentContainer>
        <ContentRow>
          <FlexField>
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => {
                const { ref, ...rest } = field;
                return (
                  <DateInput
                    key={field.value}
                    {...rest}
                    label="Benefit start date (optional)"
                    value={
                      field.value ? parseDateValue(field.value) : undefined
                    }
                    onChange={(date) =>
                      field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
                    }
                    width="100%"
                    data-testid="start-date-picker"
                  />
                );
              }}
            />
          </FlexField>
          <FlexField>
            <Controller
              name="renewalDate"
              control={control}
              render={({ field, fieldState: { isDirty } }) => {
                const { ref, ...rest } = field;
                return (
                  <DateInput
                    key={field.value}
                    {...rest}
                    label="Benefit renewal date (optional)"
                    value={
                      field.value ? parseDateValue(field.value) : undefined
                    }
                    onChange={(date) =>
                      field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
                    }
                    minDate={startDate ? parseDateValue(startDate) : undefined}
                    data-testid="renewal-date-picker"
                    width="100%"
                    disabled={isPolicyLengthSet}
                    hintText={
                      isPolicyLengthSet
                        ? 'Auto-calculated from start date + policy length'
                        : undefined
                    }
                    warningMessage={isDirty ? undefined : warnings.renewalDate}
                    successMessage={isDirty ? undefined : successes.renewalDate}
                  />
                );
              }}
            />
          </FlexField>
          <FlexField>
            <Controller
              name="policyLengthMonths"
              control={control}
              rules={{
                min: POLICY_LENGTH_MONTHS_MIN,
                max: POLICY_LENGTH_MONTHS_MAX,
              }}
              render={({ field, fieldState }) => {
                const { value, ...rest } = field;
                return (
                  <Input
                    {...rest}
                    name="Policy length in months (optional)"
                    type="number"
                    min={POLICY_LENGTH_MONTHS_MIN}
                    max={POLICY_LENGTH_MONTHS_MAX}
                    value={value ?? ''}
                    width="100%"
                    hintText={POLICY_LENGTH_MONTHS_HINT}
                    hasError={!!fieldState.error}
                    errorMessage={`Must be between ${POLICY_LENGTH_MONTHS_MIN} and ${POLICY_LENGTH_MONTHS_MAX}`}
                    data-testid="policy-length-months-input"
                  />
                );
              }}
            />
          </FlexField>
        </ContentRow>
        <ContentRow>
          <DelayFieldStack>
            <Controller
              name="activationDelay"
              control={control}
              render={({ field, fieldState }) => {
                const { value, onChange } = field;
                return (
                  <Select
                    name="Activation delay"
                    options={benefitDelayOptions}
                    optionsTooltipMaxCharCount={40}
                    value={value?.toString()}
                    onChange={onChange}
                    errorMessage={fieldCannotBeBlankErrorMessage}
                    hasError={!!fieldState.error?.message}
                    showSearch
                    width="100%"
                  />
                );
              }}
              rules={{
                required: isActive,
              }}
            />
            <Controller
              name="delayReferenceMode"
              control={control}
              render={({ field }) => {
                const { value, onChange } = field;
                const handleChange = (next?: string) => onChange(next ?? null);
                return (
                  <Select
                    name="Activation delay anchor (optional)"
                    options={delayReferenceModeOptions}
                    optionsTooltipMaxCharCount={40}
                    hintText={DELAY_REFERENCE_MODE_HINT}
                    value={value ?? undefined}
                    onChange={handleChange}
                    clearable
                    width="100%"
                  />
                );
              }}
            />
          </DelayFieldStack>
          <DelayField>
            <Controller
              name="changeDelay"
              control={control}
              render={({ field }) => {
                const { value, onChange } = field;
                const handleChange = (next?: string | null) =>
                  onChange(next ?? '');
                return (
                  <Select
                    name="Change delay (optional)"
                    options={benefitDelayOptions}
                    optionsTooltipMaxCharCount={40}
                    value={value === '' ? undefined : value?.toString()}
                    onChange={handleChange}
                    clearable
                    showSearch
                    width="100%"
                  />
                );
              }}
            />
          </DelayField>
          <DelayField>
            <Controller
              name="deactivationDelay"
              control={control}
              render={({ field, fieldState }) => {
                const { value, onChange } = field;
                return (
                  <Select
                    name="Deactivation delay"
                    options={benefitDelayOptions}
                    optionsTooltipMaxCharCount={40}
                    value={value?.toString()}
                    onChange={onChange}
                    errorMessage={fieldCannotBeBlankErrorMessage}
                    hasError={!!fieldState.error?.message}
                    showSearch
                    width="100%"
                  />
                );
              }}
              rules={{
                required: isActive,
              }}
            />
          </DelayField>
        </ContentRow>

        <ContentRow>
          <Controller
            name="autoActivating"
            control={control}
            render={({ field, fieldState: { isDirty } }) => {
              return (
                <Checkbox
                  label="Auto activating"
                  {...field}
                  value={field.value || false}
                  successMessage={
                    isDirty ? undefined : successes.autoActivating
                  }
                />
              );
            }}
          />
          <Controller
            name="deactivateEnrolmentsWithProgramme"
            control={control}
            render={({ field }) => {
              return (
                <Checkbox
                  label="Deactivate enrolments with programme"
                  {...field}
                  value={field.value ?? true}
                />
              );
            }}
          />
        </ContentRow>
        <ContentRow>
          <Controller
            name="requiresActivationApproval"
            control={control}
            render={({ field, fieldState: { isDirty } }) => {
              return (
                <Checkbox
                  label="Requires activation approval"
                  {...field}
                  value={field.value || false}
                  successMessage={
                    isDirty ? undefined : successes.requiresApproval
                  }
                />
              );
            }}
          />
          <Controller
            name="requiresDeactivationApproval"
            control={control}
            render={({ field, fieldState: { isDirty } }) => {
              return (
                <Checkbox
                  label="Requires deactivation approval"
                  {...field}
                  value={field.value || false}
                  successMessage={
                    isDirty ? undefined : successes.requiresApproval
                  }
                />
              );
            }}
          />
        </ContentRow>

        <CheckboxList>
          <Typography variant={TypographyVariant.bodySm2}>
            Sections to show (optional)
          </Typography>
          <Controller
            name="showEnrolments"
            control={control}
            render={({ field }) => {
              return (
                <Checkbox
                  label="Show enrolments"
                  {...field}
                  value={field.value || false}
                />
              );
            }}
          />
          <Controller
            name="showChanges"
            control={control}
            render={({ field }) => {
              return (
                <Checkbox
                  label="Show changes"
                  {...field}
                  value={field.value || false}
                />
              );
            }}
          />
          <Controller
            name="showPaymentHistory"
            control={control}
            render={({ field }) => {
              return (
                <Checkbox
                  label="Show payment"
                  {...field}
                  value={field.value || false}
                />
              );
            }}
          />
          <Controller
            name="enableRequests"
            control={control}
            render={({ field }) => {
              return (
                <Checkbox
                  label="Enable requests"
                  {...field}
                  value={field.value || false}
                />
              );
            }}
          />
        </CheckboxList>
      </ContentContainer>
    </Container>
  );
};

export default memo(EnrolmentSettings);

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(3)};
    border: 1px solid ${theme.palette.neutral[60]};
    border-radius: 10px;
    gap: ${theme.spacing(3)};
    width: 100%;
  `};
`;

const HeaderContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `};
`;

const ContentContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    border-radius: 10px;
    background-color: ${theme.palette.primary.bg};
    padding: ${theme.spacing(3)};
    width: 100%;
  `};
`;

const ContentRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `};
`;

const CheckboxList = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `};
`;

const FlexField = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

// Keeps each delay an equal share of the row so a stacked cell does not widen
// its column relative to the single-field cells beside it.
const DelayField = styled.div`
  flex: 1;
  min-width: 0;
`;

const DelayFieldStack = styled(DelayField)`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `};
`;
